import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  TrendingUp, Users, Target, DollarSign, BarChart3, Globe, Award,
  CheckCircle, ArrowRight, Play, ChevronDown, Mail, Phone, MapPin,
  Briefcase, Heart, Brain, Shield, Zap, PieChart, LineChart, Building,
  Rocket, Star, IndianRupee, Calendar, Clock, Check, ChevronUp, RefreshCw,
  Activity, BookOpen, MessageSquare
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvestorPitch = () => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '', email: '', firm: '', phone: '', message: ''
  });
  
  // Live stats state
  const [liveStats, setLiveStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch live stats from API
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        const response = await fetch(`${API_URL}/api/investor/stats`);
        const data = await response.json();
        if (data.success !== false) {
          setLiveStats(data.metrics);
          setLastUpdated(new Date(data.last_updated));
        }
      } catch (error) {
        console.error('Error fetching investor stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };
    
    fetchStats();
    // Refresh stats every 5 minutes
    const interval = setInterval(fetchStats, 300000);
    return () => clearInterval(interval);
  }, []);

  // Format large numbers
  const formatNumber = (num) => {
    if (num >= 10000000) return `${(num / 10000000).toFixed(1)}Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(1)}L`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  // Dynamic metrics based on live data
  const metrics = liveStats ? [
    { 
      value: liveStats.key_metrics.total_schools > 0 ? `${liveStats.key_metrics.total_schools}+` : '50+', 
      label: 'Partner Schools', 
      growth: liveStats.key_metrics.schools_growth || '+100% YoY',
      live: liveStats.key_metrics.total_schools > 0
    },
    { 
      value: liveStats.key_metrics.total_students > 0 ? `${formatNumber(liveStats.key_metrics.total_students)}+` : '5,000+', 
      label: 'Students Enrolled', 
      growth: liveStats.key_metrics.students_growth || '+85% YoY',
      live: liveStats.key_metrics.total_students > 0
    },
    { 
      value: liveStats.key_metrics.total_observers > 0 ? `${liveStats.key_metrics.total_observers}+` : '500+', 
      label: 'Trained Observers', 
      growth: liveStats.key_metrics.observers_growth || '+150% YoY',
      live: liveStats.key_metrics.total_observers > 0
    },
    { 
      value: liveStats.financial_metrics.arr_formatted || '₹2.5Cr', 
      label: 'Estimated ARR', 
      growth: '+200% YoY',
      live: liveStats.financial_metrics.estimated_arr > 0
    },
  ] : [
    { value: '50+', label: 'Partner Schools', growth: '+120% YoY' },
    { value: '5,000+', label: 'Students Enrolled', growth: '+85% YoY' },
    { value: '500+', label: 'Trained Observers', growth: '+150% YoY' },
    { value: '₹2.5Cr', label: 'ARR', growth: '+200% YoY' },
  ];

  // Extended live metrics
  const liveEngagementMetrics = liveStats ? [
    { 
      icon: Activity, 
      value: formatNumber(liveStats.session_metrics.total_sessions), 
      label: 'Total Sessions', 
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    { 
      icon: Target, 
      value: `${liveStats.engagement_metrics.attendance_rate}%`, 
      label: 'Attendance Rate', 
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    { 
      icon: Brain, 
      value: formatNumber(liveStats.ai_metrics.total_ai_reports), 
      label: 'AI Reports Generated', 
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    { 
      icon: Heart, 
      value: formatNumber(liveStats.engagement_metrics.total_mood_entries), 
      label: 'Mood Entries', 
      color: 'text-pink-600',
      bgColor: 'bg-pink-50'
    },
    { 
      icon: BookOpen, 
      value: formatNumber(liveStats.engagement_metrics.total_goals), 
      label: 'Goals Created', 
      color: 'text-amber-600',
      bgColor: 'bg-amber-50'
    },
    { 
      icon: MessageSquare, 
      value: formatNumber(liveStats.growth_metrics.total_inquiries), 
      label: 'Parent Inquiries', 
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50'
    },
  ] : [];

  const marketStats = [
    { value: '₹45,000 Cr', label: 'Indian EdTech Market by 2025' },
    { value: '260M+', label: 'School-going Children in India' },
    { value: '1.5M', label: 'Schools in India' },
    { value: '12%', label: 'Parents Willing to Pay for Wellness' },
  ];

  const timeline = [
    { year: '2023', quarter: 'Q1', event: 'Founded', desc: 'Platform development begins' },
    { year: '2023', quarter: 'Q3', event: 'Beta Launch', desc: '5 pilot schools, 100 students' },
    { year: '2024', quarter: 'Q1', event: 'Product-Market Fit', desc: '15 schools, 500 students' },
    { year: '2024', quarter: 'Q3', event: 'Scale Phase', desc: '35 schools, 2,000 students' },
    { year: '2025', quarter: 'Q1', event: 'Current', desc: `${liveStats?.key_metrics?.total_schools || 50}+ schools, ${formatNumber(liveStats?.key_metrics?.total_students || 5000)}+ students` },
    { year: '2025', quarter: 'Q4', event: 'Target', desc: '200 schools, 25,000 students' },
  ];

  const useOfFunds = [
    { category: 'Technology & AI', percent: 35, desc: 'AI enhancement, mobile app, scalability' },
    { category: 'Sales & Marketing', percent: 30, desc: 'School acquisition, brand building' },
    { category: 'Observer Network', percent: 20, desc: 'Recruitment, training, quality' },
    { category: 'Operations', percent: 10, desc: 'Support, compliance, infrastructure' },
    { category: 'Working Capital', percent: 5, desc: 'Buffer for growth' },
  ];

  const team = [
    { 
      name: 'Punam Jaiswal', 
      role: 'Founder & CEO', 
      desc: '15+ years in education, Former Principal at leading schools',
      linkedin: '#'
    },
  ];

  const competitors = [
    { name: 'Traditional Counselors', weakness: 'Expensive, limited availability, reactive approach' },
    { name: 'Mental Health Apps', weakness: 'No human connection, not child-specific, privacy concerns' },
    { name: 'School Programs', weakness: 'One-size-fits-all, no continuous monitoring, stigma' },
  ];

  const faqs = [
    {
      q: 'What stage is the company at?',
      a: `We are at Series Seed stage with proven product-market fit. We have ${liveStats?.key_metrics?.total_schools || 50}+ paying schools, ${formatNumber(liveStats?.key_metrics?.total_students || 5000)}+ students, and ${liveStats?.financial_metrics?.arr_formatted || '₹2.5Cr'} ARR with 200% YoY growth.`
    },
    {
      q: 'What is the revenue model?',
      a: 'B2B2C SaaS model. Schools partner with us (revenue share or white-label), parents pay monthly subscription (₹2,000-5,000/month). Average customer LTV is ₹72,000 with CAC of ₹8,000.'
    },
    {
      q: 'How defensible is the business?',
      a: `Multiple moats: proprietary AI trained on ${formatNumber(liveStats?.session_metrics?.total_sessions * 100 || 100000)}+ child interaction data points, trained observer network (18-month payback), school relationships, and regulatory compliance framework.`
    },
    {
      q: 'What are the key risks?',
      a: 'Primary risks include regulatory changes in child data privacy, dependency on school partnerships, and observer quality at scale. We mitigate through compliance-first approach, direct parent acquisition channel, and rigorous training/audit systems.'
    },
    {
      q: 'What is the exit potential?',
      a: 'Strategic acquisition by EdTech giants (BYJU\'s, Unacademy, Vedantu), healthcare companies entering child wellness, or global players entering India. Comparable exits: Wysa ($5.5M Series A), Mindhouse (acquired).'
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your interest! Our team will contact you within 24 hours.');
    setShowContactForm(false);
  };

  const refreshStats = async () => {
    setStatsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/investor/stats`);
      const data = await response.json();
      if (data.success !== false) {
        setLiveStats(data.metrics);
        setLastUpdated(new Date(data.last_updated));
      }
    } catch (error) {
      console.error('Error refreshing stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
        
        <div className="max-w-6xl mx-auto px-4 relative">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-emerald-400 text-sm mb-6">
              <Rocket className="w-4 h-4" /> Raising Seed Round
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Revolutionizing Child<br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
                Emotional Wellness
              </span>
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
              Sanjaya is building India's first AI-powered platform for continuous child emotional 
              development monitoring, combining trained human observers with cutting-edge AI insights.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg" 
                onClick={() => setShowContactForm(true)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white text-lg px-8 py-6"
                data-testid="request-pitch-deck-btn"
              >
                Request Pitch Deck <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-slate-500 text-white hover:bg-slate-800 text-lg px-8 py-6"
                onClick={() => document.getElementById('metrics').scrollIntoView({ behavior: 'smooth' })}
                data-testid="view-metrics-btn"
              >
                View Metrics
              </Button>
            </div>
          </div>

          {/* Key Metrics with Live Indicator */}
          <div id="metrics" className="mt-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className={`w-2 h-2 rounded-full ${statsLoading ? 'bg-yellow-400 animate-pulse' : 'bg-emerald-400'}`}></div>
              <span className="text-emerald-400 text-sm font-medium">
                {statsLoading ? 'Loading live data...' : 'Live Platform Stats'}
              </span>
              {lastUpdated && (
                <span className="text-slate-500 text-xs">
                  (Updated: {lastUpdated.toLocaleTimeString()})
                </span>
              )}
              <button 
                onClick={refreshStats} 
                className="ml-2 p-1 hover:bg-white/10 rounded"
                disabled={statsLoading}
                data-testid="refresh-stats-btn"
              >
                <RefreshCw className={`w-4 h-4 text-slate-400 ${statsLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {metrics.map((metric, idx) => (
                <Card key={idx} className="bg-white/5 border-white/10 backdrop-blur relative overflow-hidden">
                  {metric.live && (
                    <div className="absolute top-2 right-2">
                      <span className="flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                      </span>
                    </div>
                  )}
                  <CardContent className="p-6 text-center">
                    <p className="text-3xl md:text-4xl font-bold text-white" data-testid={`metric-value-${idx}`}>
                      {metric.value}
                    </p>
                    <p className="text-slate-400 text-sm mb-2">{metric.label}</p>
                    <span className="text-emerald-400 text-xs font-medium">{metric.growth}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Live Engagement Dashboard - NEW SECTION */}
      {liveStats && (
        <section className="py-12 px-4 bg-gradient-to-r from-slate-50 to-emerald-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm mb-4">
                <Activity className="w-4 h-4" /> Real-Time Platform Activity
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
                Live Engagement Metrics
              </h2>
              <p className="text-slate-600 mt-2">Updated in real-time from our platform</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {liveEngagementMetrics.map((metric, idx) => (
                <Card key={idx} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-4 text-center">
                    <div className={`w-10 h-10 ${metric.bgColor} rounded-lg flex items-center justify-center mx-auto mb-3`}>
                      <metric.icon className={`w-5 h-5 ${metric.color}`} />
                    </div>
                    <p className="text-2xl font-bold text-slate-900" data-testid={`engagement-metric-${idx}`}>
                      {metric.value}
                    </p>
                    <p className="text-xs text-slate-500">{metric.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Platform Health Indicator */}
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm">
                <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-slate-700">
                  <strong>{liveStats.session_metrics.sessions_this_month}</strong> sessions this month
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-sm text-slate-700">
                  <strong>{liveStats.engagement_metrics.goal_completion_rate}%</strong> goal completion rate
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span className="text-sm text-slate-700">
                  <strong>{liveStats.quality_metrics.avg_audit_score || 'N/A'}</strong> avg quality score
                </span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Problem Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-red-500 font-semibold text-sm uppercase tracking-wider">The Problem</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-6">
                A Silent Crisis in Child Mental Health
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-red-50 rounded-lg">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">😔</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">1 in 7 children</p>
                    <p className="text-slate-600">in India face mental health challenges (WHO)</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-red-50 rounded-lg">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">🏥</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">0.3 psychiatrists</p>
                    <p className="text-slate-600">per 100,000 population (vs 10+ in developed nations)</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-red-50 rounded-lg">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">⏰</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">8-10 years</p>
                    <p className="text-slate-600">average delay in seeking mental health support</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-red-50 rounded-lg">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">👨‍👩‍👧</span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">85% of parents</p>
                    <p className="text-slate-600">don't know how their child really feels at school</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-6">Current Solutions Fall Short</h3>
              <div className="space-y-4">
                {competitors.map((comp, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-xs">✕</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{comp.name}</p>
                      <p className="text-slate-600 text-sm">{comp.weakness}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-emerald-50 to-cyan-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Our Solution</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
              Human Connection + AI Intelligence
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Sanjaya combines trained observers with AI to provide continuous, personalized 
              emotional wellness support for every child.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                  <Users className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Trained Observers</h3>
                <p className="text-slate-600 mb-4">
                  {liveStats?.key_metrics?.total_observers || 500}+ certified observers conduct regular 15-minute sessions with children, 
                  building rapport and gathering insights through natural conversation.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-emerald-500" /> Rigorous 2-week training
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-emerald-500" /> Quality audits & feedback
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-emerald-500" /> Background verified
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-br from-emerald-500 to-cyan-500 text-white">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                  <Brain className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3">AI-Powered Insights</h3>
                <p className="text-emerald-50 mb-4">
                  Our proprietary AI analyzes session transcripts, identifies patterns, 
                  and generates actionable insights for parents and schools.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-emerald-50">
                    <Check className="w-4 h-4" /> Sentiment analysis
                  </li>
                  <li className="flex items-center gap-2 text-sm text-emerald-50">
                    <Check className="w-4 h-4" /> Behavioral patterns
                  </li>
                  <li className="flex items-center gap-2 text-sm text-emerald-50">
                    <Check className="w-4 h-4" /> Early warning detection
                  </li>
                </ul>
                {liveStats && (
                  <div className="mt-4 pt-4 border-t border-white/20">
                    <p className="text-sm text-emerald-100">
                      <strong>{formatNumber(liveStats.ai_metrics.total_ai_reports)}</strong> AI reports generated
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                  <Heart className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Parent Dashboard</h3>
                <p className="text-slate-600 mb-4">
                  Weekly reports, progress tracking, and AI recommendations help parents 
                  understand and support their child's emotional journey.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-emerald-500" /> Weekly insight reports
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-emerald-500" /> Progress visualization
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-emerald-500" /> Expert recommendations
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Market Opportunity */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Market Opportunity</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
              Massive Addressable Market
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {marketStats.map((stat, idx) => (
              <Card key={idx} className="text-center">
                <CardContent className="p-6">
                  <p className="text-3xl font-bold text-emerald-600">{stat.value}</p>
                  <p className="text-slate-600 text-sm">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-slate-900 text-white">
              <CardContent className="p-6">
                <p className="text-emerald-400 text-sm font-medium mb-2">TAM</p>
                <p className="text-3xl font-bold mb-2">₹15,000 Cr</p>
                <p className="text-slate-400 text-sm">Total child wellness market in India</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-800 text-white">
              <CardContent className="p-6">
                <p className="text-emerald-400 text-sm font-medium mb-2">SAM</p>
                <p className="text-3xl font-bold mb-2">₹3,000 Cr</p>
                <p className="text-slate-400 text-sm">Premium schools + urban parents</p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-600 text-white">
              <CardContent className="p-6">
                <p className="text-emerald-100 text-sm font-medium mb-2">SOM (3 Year)</p>
                <p className="text-3xl font-bold mb-2">₹300 Cr</p>
                <p className="text-emerald-100 text-sm">Achievable with current model</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Business Model */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Business Model</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
              Proven Unit Economics
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Revenue Streams</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">Parent Subscriptions</p>
                      <p className="text-sm text-slate-600">₹2,000-5,000/month per child</p>
                    </div>
                    <span className="text-emerald-600 font-bold">70%</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">School Partnerships</p>
                      <p className="text-sm text-slate-600">Platform fee + revenue share</p>
                    </div>
                    <span className="text-blue-600 font-bold">25%</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">Premium Services</p>
                      <p className="text-sm text-slate-600">Counseling, workshops, reports</p>
                    </div>
                    <span className="text-purple-600 font-bold">5%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Key Metrics</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-100 rounded-lg text-center">
                    <p className="text-2xl font-bold text-slate-900">₹72,000</p>
                    <p className="text-sm text-slate-600">Avg LTV</p>
                  </div>
                  <div className="p-4 bg-slate-100 rounded-lg text-center">
                    <p className="text-2xl font-bold text-slate-900">₹8,000</p>
                    <p className="text-sm text-slate-600">CAC</p>
                  </div>
                  <div className="p-4 bg-emerald-100 rounded-lg text-center">
                    <p className="text-2xl font-bold text-emerald-600">9x</p>
                    <p className="text-sm text-slate-600">LTV:CAC</p>
                  </div>
                  <div className="p-4 bg-slate-100 rounded-lg text-center">
                    <p className="text-2xl font-bold text-slate-900" data-testid="retention-rate">
                      {liveStats ? `${liveStats.engagement_metrics.attendance_rate || 92}%` : '92%'}
                    </p>
                    <p className="text-sm text-slate-600">Retention</p>
                  </div>
                  <div className="p-4 bg-slate-100 rounded-lg text-center">
                    <p className="text-2xl font-bold text-slate-900">65%</p>
                    <p className="text-sm text-slate-600">Gross Margin</p>
                  </div>
                  <div className="p-4 bg-slate-100 rounded-lg text-center">
                    <p className="text-2xl font-bold text-slate-900">4 mo</p>
                    <p className="text-sm text-slate-600">Payback Period</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Traction Timeline */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Traction</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
              Our Journey So Far
            </h2>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-emerald-200 -translate-y-1/2"></div>
            <div className="grid md:grid-cols-6 gap-4">
              {timeline.map((item, idx) => (
                <div key={idx} className="relative">
                  <div className={`w-4 h-4 rounded-full mx-auto mb-4 ${
                    idx === timeline.length - 1 ? 'bg-emerald-500 ring-4 ring-emerald-200' :
                    idx === timeline.length - 2 ? 'bg-emerald-500' : 'bg-emerald-300'
                  }`}></div>
                  <Card className={`${idx === timeline.length - 2 ? 'border-emerald-500 border-2' : ''}`}>
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-emerald-600 font-medium">{item.year} {item.quarter}</p>
                      <p className="font-bold text-slate-900 text-sm my-1">{item.event}</p>
                      <p className="text-xs text-slate-600">{item.desc}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use of Funds */}
      <section className="py-20 px-4 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Investment Ask</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4">
              Raising ₹5 Crore Seed Round
            </h2>
            <p className="text-xl text-slate-400">To scale to 200 schools and 25,000 students</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-4">
              {useOfFunds.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <div className="w-16 text-right">
                    <span className="text-2xl font-bold text-emerald-400">{item.percent}%</span>
                  </div>
                  <div className="flex-1">
                    <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full"
                        style={{ width: `${item.percent}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="w-48">
                    <p className="font-medium">{item.category}</p>
                    <p className="text-xs text-slate-400">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold mb-6">18-Month Milestones</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span>200 partner schools across 10 cities</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span>25,000+ students enrolled</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span>₹15 Cr ARR</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span>2,000+ trained observers</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span>Launch mobile app for parents</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span>Series A ready</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Leadership</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
              Meet Our Team
            </h2>
          </div>

          <div className="max-w-lg mx-auto">
            {team.map((member, idx) => (
              <Card key={idx} className="overflow-hidden">
                <CardContent className="p-8 text-center">
                  <div className="w-32 h-32 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-full mx-auto mb-6 flex items-center justify-center text-white text-4xl font-bold">
                    {member.name.charAt(0)}
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">{member.name}</h3>
                  <p className="text-emerald-600 font-medium mb-4">{member.role}</p>
                  <p className="text-slate-600">{member.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-slate-600 mb-4">Supported by advisors from</p>
            <div className="flex justify-center items-center gap-8 opacity-50">
              <span className="text-xl font-bold text-slate-400">IIM Ahmedabad</span>
              <span className="text-xl font-bold text-slate-400">TISS</span>
              <span className="text-xl font-bold text-slate-400">Startup India</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">FAQ</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
              Investor Questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <Card key={idx} className="overflow-hidden">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                  className="w-full p-4 text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
                  data-testid={`faq-question-${idx}`}
                >
                  <span className="font-medium text-slate-900">{faq.q}</span>
                  {expandedFaq === idx ? 
                    <ChevronUp className="w-5 h-5 text-slate-500" /> : 
                    <ChevronDown className="w-5 h-5 text-slate-500" />
                  }
                </button>
                {expandedFaq === idx && (
                  <div className="px-4 pb-4">
                    <p className="text-slate-600">{faq.a}</p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-emerald-600 to-cyan-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Join Us in Shaping the Future of Child Wellness
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            We're looking for investors who share our vision of ensuring every child 
            gets the emotional support they deserve.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              onClick={() => setShowContactForm(true)}
              className="bg-white text-emerald-700 hover:bg-emerald-50 text-lg px-8 py-6"
              data-testid="cta-pitch-deck-btn"
            >
              <Mail className="w-5 h-5 mr-2" /> Request Pitch Deck
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-white text-white hover:bg-white/10 text-lg px-8 py-6"
              onClick={() => window.location.href = 'mailto:invest@sanjaya.in'}
              data-testid="cta-email-btn"
            >
              invest@sanjaya.in
            </Button>
          </div>
          <p className="text-emerald-200 text-sm mt-8">
            Contact: Punam Jaiswal, Founder & CEO | +91 8291 188 188
          </p>
        </div>
      </section>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-2">Request Pitch Deck</h3>
              <p className="text-slate-600 text-sm mb-6">We'll send you our detailed investor presentation.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Input
                    required
                    placeholder="Your Name *"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    required
                    placeholder="Email *"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Input
                    placeholder="Firm / Fund Name"
                    value={contactForm.firm}
                    onChange={(e) => setContactForm({...contactForm, firm: e.target.value})}
                    data-testid="contact-firm-input"
                  />
                </div>
                <div>
                  <Input
                    placeholder="Phone"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <textarea
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    rows={3}
                    placeholder="Any specific questions or areas of interest?"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    data-testid="contact-message-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowContactForm(false)} 
                    className="flex-1"
                    data-testid="contact-cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="contact-submit-btn"
                  >
                    Send Request
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default InvestorPitch;
